import { Box, CircularProgress, SxProps, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { createZone, updateZone } from "../api/requests/zones";
import { useAlert } from "../context/AlertContext";
import { commonStrings } from "../static/commonStrings";
import { RIOType, ZoneType } from "../utils/types";
import CommonDialog from "./common/CommonDialog";

interface CreateOrEditZoneProps {
  setOpenCreateDialog: () => void;
  onSaveZoneCB: () => void;
  isEdit?: boolean;
  zoneTypeData?: ZoneType | null;
  initialSelectedRIO: RIOType;
}

const CreateOrEditZone = ({
  setOpenCreateDialog,
  onSaveZoneCB,
  isEdit = false,
  zoneTypeData,
  initialSelectedRIO,
}: CreateOrEditZoneProps) => {
  const [zoneName, setZoneName] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const { showAlert } = useAlert();
  useEffect(() => {
    if (zoneTypeData) {
      if (isEdit) {
        setZoneName(zoneTypeData.zoneName || "");
      }
    }
  }, [isEdit, zoneTypeData]);

  const handleZoneNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setZoneName(event.target.value);
  };

  const handleSaveZone = async () => {
    if (!initialSelectedRIO.rioId) {
      showAlert("Please select an RIO", "error");
      return;
    }

    if (!zoneName.trim()) {
      showAlert("Please enter a Zone name", "error");
      return;
    }

    try {
      setLoading(true);
      const createZoneRes = await createZone({
        rioId: Number(initialSelectedRIO.rioId),
        zoneName: zoneName.trim(),
      });

      if (createZoneRes.status === 200) {
        showAlert("Zone added successfully", "success");
        setOpenCreateDialog();
        onSaveZoneCB();
      } else {
        showAlert("Failed to create zone", "error");
      }
    } catch (error) {
      console.error("Create zone error:", error);
      showAlert("Failed to create zone", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEditZone = async () => {
    if (!initialSelectedRIO.rioId) {
      showAlert("Please select an RIO", "error");
      return;
    }

    if (!zoneName.trim()) {
      showAlert("Please enter a Zone name", "error");
      return;
    }

    if (!zoneTypeData?.zoneId) {
      showAlert("Zone ID is missing", "error");
      return;
    }

    try {
      setLoading(true);
      const editZoneRes = await updateZone({
        rioId: initialSelectedRIO.rioId,
        zoneId: zoneTypeData.zoneId,
        zoneName: zoneName.trim(),
      });

      if (editZoneRes.status === 200) {
        showAlert("Zone updated successfully", "success");
        setOpenCreateDialog();
        onSaveZoneCB();
      } else {
        showAlert("Failed to update zone", "error");
      }
    } catch (error) {
      console.error("Update zone error:", error);
      showAlert("Failed to update zone", "error");
    } finally {
      setLoading(false);
    }
  };

  const dialogTitle = `${isEdit ? "Edit" : "Create"} ${
    commonStrings.sideBarMenuTitles.zone
  }`;

  const isFormValid = !!(initialSelectedRIO.rioId && zoneName.trim());
  return (
    <>
      <CommonDialog
        title={dialogTitle}
        cancelButton={() => setOpenCreateDialog()}
        actionButtonTitle="Save"
        cancelButtonTitle="Cancel"
        actionButton={isEdit ? handleEditZone : handleSaveZone}
        disableActionButton={!isFormValid || loading}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}>
          <Box>
            <TextField
              fullWidth
              required
              label={`Enter ${commonStrings.sideBarMenuTitles.rio}`}
              value={initialSelectedRIO?.rioName}
              disabled
            />
          </Box>
          <Box>
            <TextField
              fullWidth
              required
              label={`Enter ${commonStrings.sideBarMenuTitles.zone}`}
              value={zoneName}
              onChange={handleZoneNameChange}
              disabled={loading}
            />
          </Box>
        </Box>
      </CommonDialog>

      {loading && (
        <Box sx={loadingBoxStyles}>
          <CircularProgress size={60} thickness={6} />
        </Box>
      )}
    </>
  );
};

export default CreateOrEditZone;

const loadingBoxStyles: SxProps = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  zIndex: 9999,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  bgcolor: "rgba(0, 0, 0, 0.5)",
  width: "100vw",
  height: "100vh",
};
